import { autoinject, transient, BindingEngine, computedFrom } from "aurelia-framework";
import { Router } from "aurelia-router";
import { I18N } from "aurelia-i18n";
import { NotificationClientService, NotificationType } from "../../../../services/basics/notification-client.service";
import { EventAggregator } from "aurelia-event-aggregator";
import { ValidationsClientService } from "../../../../services/validations/validations-client.service";
import { RightShipQiRouterConstants } from "../../../../configuration/rightshipqi-router.constants";
import { StepModel } from "../../../../models/basics/basics.models";
import { EntityTypes } from "@rightship-tech/common-ui/models/basic.models";
import { BaseValidationResponse } from "../../../../models/validations/validations.models";
import { PscOverview } from "./steps/overview/psc-overview";
import { PscStepEnum, PscClientModel, type PscDeficiencyStatusClientModel, PscActivitiesColumns } from "../../../../models/pscs/psc.models";
import { PscService } from "../../../../services/psc/psc.service";
import { PscDocuments } from "./steps/documents/psc-documents";
import { DialogClientService } from "@rightship-tech/common-ui/services/basic/dialog-client.service";
import { PscDeficiencies } from "./steps/deficiencies/psc-deficiencies";
import { Reflection } from "../../../../utilities/reflection";
import { ActiveMenuItemsService } from "../../../../services/basics/active-menu-items.service";
import { MenuItem } from "../../../components/menu/menu-item";
import { RightShipDialogServiceConstants } from "../../../../constants/basics/rightship-dialog.constants";
import { UserClientService } from "../../../../services/authentication/user-client.service";
import * as moment from "moment";
import { Assignment } from "../../../components/assignment/assignment";
import { LookupValue, LookupValueRequest } from "../../../../models/lookups/lookup.models";
import { LookupValueQuery } from "../../../../constants/modules/lookup.constants";
import { LookupClientService } from "../../../../services/lookups/lookup-client.service";
import { EntityStatusChangeModel, SignalFunctions } from "../../../../services/basics/signal-functions";
import { SignalClientService } from "@rightship-tech/common-ui/services/basic/signal-client.service";
import { Claims } from "../../../../constants/authentication/claims";
import { UserClaimsService } from "../../../../services/authentication/user-claims.service";
import { PscInspectionStatusEnum } from "../../../../models/vetting/vetting.models";
import { TasksStepsEnum, TasksConstants } from "../../../../models/tasks/task.model";
import { CacheClientService } from "@rightship-tech/common-ui/services/basic/client-caching.service";
import { ClipboardCopyService } from "../../../../utilities/clipboard-copy-service";
import { Correspondence } from "../common-steps/correspondence/correspondence";
import { EntityComments } from "../../common/entity-notes/entity-notes";
import { TitleWithCountHelper } from "../../../../helpers/title-with-count-helper";
import { PscHistoryTrail } from "./steps/psc-history-trail/psc-history-trail";
import { EmailHistory } from "../common-steps/email-history/email-history";
import { ManageVesselService } from "../../../../services/vessels/manage-vessel.service";
import { RatingBaseEntity } from "../common-steps/base-rating-entity";
import { Tabs } from "../../../components/tabs/tabs";
import { Guid } from "../../../../utilities/guid";
import { CompCorrespondenceModel } from "../../../../models/vessels/vessel.models";

type PSCSource = 'vessels' | 'activities';

@autoinject
@transient()
export class PSC extends RatingBaseEntity<PscClientModel> {

    readonly: boolean = true;
    canChangePscInspectionStatus: boolean;
    assignment: Assignment;
    viewPSCCorrespondence: boolean;
    viewPSCEmailHistory: boolean;
    canRequireRightShipInspection: boolean;
    canOverridePscDetails: boolean;
    canAssignPSC: boolean;
    canViewPSCDocuments: boolean;
    canViewPSCOverrideHistory: boolean;
    pscInspectionStatuses: LookupValue[];
    pscInspectionMisreportedReasons: LookupValue[];
    reopeningPsc: boolean = false;
    forceLoadVesselHeader: boolean = false;
    rightShipUser: boolean;
    entityItem: PSC;

    constructor(protected router: Router,
        protected dialogService: DialogClientService,
        protected bindingEngine: BindingEngine,
        protected i18N: I18N,
        protected notificationService: NotificationClientService,
        protected eventAggregator: EventAggregator,
        protected validationsClientService: ValidationsClientService,
        protected pscOverview: PscOverview,
        protected pscDeficiencies: PscDeficiencies,
        protected pscDocuments: PscDocuments,
        protected entityComments: EntityComments,
        protected correspondence: Correspondence,
        protected emailHistory: EmailHistory,
        protected pscHistoryTrail: PscHistoryTrail,
        private pscService: PscService,
        private userClientService: UserClientService,
        protected signalClientService: SignalClientService,
        private cacheClient: CacheClientService,
        private clipboardCopyService: ClipboardCopyService,
        lookupClientService: LookupClientService,
        private userClaimsService: UserClaimsService,
        private activeMenuItemsService: ActiveMenuItemsService,
        protected manageVesselService: ManageVesselService) {
        super(router, bindingEngine, i18N, notificationService, eventAggregator, validationsClientService, signalClientService, manageVesselService);
        this.entityComments.canEdit = true;
        this.entityComments.canDelete = true;
        this.canChangePscInspectionStatus = userClaimsService.hasPermission(Claims.CanChangePscInspectionStatus);
        this.viewPSCCorrespondence = userClaimsService.hasPermission(Claims.CanViewPSCCorrespondence);
        this.viewPSCEmailHistory = userClaimsService.hasPermission(Claims.CanViewPSCEmailHistory);
        this.canRequireRightShipInspection = userClaimsService.hasPermission(Claims.CanRequireRightShipInspection);
        this.canOverridePscDetails = userClaimsService.hasPermission(Claims.CanOverridePscDetails);
        this.canAssignPSC = userClaimsService.hasPermission(Claims.CanAssignPSC);
        this.canViewPSCDocuments = userClaimsService.hasPermission(Claims.CanViewPSCDocuments);
        this.canViewPSCOverrideHistory = userClaimsService.hasPermission(Claims.CanViewPSCOverrideHistory);
        this.reopeningPsc = userClaimsService.hasPermission(Claims.ReopeningPsc);
        this.rightShipUser = userClaimsService.hasPermission(Claims.RightShipUser);
        const request = new LookupValueRequest();
        request.lookupValueQuery = LookupValueQuery.PscInspectionStatuses;
        lookupClientService.getLookup(request).then(x => {
            this.pscInspectionStatuses = x.activeValues;
        });
        lookupClientService.getLookup(new LookupValueRequest(LookupValueQuery.PscInspectionMisreportedReasons)).then(x => {
            this.pscInspectionMisreportedReasons = x.activeValues;
        });

    }

    @computedFrom("currentStatus.statusId")
    public get isMisreported(): boolean {
        if (!this.currentStatus || !this.currentStatus.statusId) return true;

        return this.currentStatus.statusId == PscInspectionStatusEnum.Misreported;
    }

    @computedFrom("item.closed")
    public get isClosed(): boolean {
        return this.item.closed;
    }

    @computedFrom("item.statuses.length")
    public get currentStatus(): PscDeficiencyStatusClientModel {
        if (this.item && this.item.statuses && this.item.statuses.length > 0) {
            return this.item.statuses[0];
        } else return null;
    }

    @computedFrom("currentStatus.statusId")
    public get inProgress(): boolean {
        return this.currentStatus.statusId == PscInspectionStatusEnum.InProgress;
    }

    @computedFrom(Reflection.getPropertyPath<PSC>(x => x.item), Reflection.getPropertyPath<PSC>(x => x.item.vesselDateOfBuild))
    public get age() {
        if (this.item) {
            if (this.item.vesselDateOfBuild) {
                return (Math.floor(moment().diff(this.item.vesselDateOfBuild, 'year', true) * 10) / 10).toFixed(1) + " y/o"
            } else {
                return ""
            }
        } else {
            return null;
        }
    }

    @computedFrom(Reflection.getPropertyPath<PSC>(p => p.item.vesselId))
    public get vesselUrl() {
        return this.router.generate(RightShipQiRouterConstants.RouteNames.VesselDetails, {
            id: this.item.vesselId,
        });
    }

    @computedFrom(Reflection.getPropertyPath<PSC>(v => v.router.currentInstruction))
    public get previousInstructionTitle(): string {
        const source: PSCSource = this._params.source;

        switch (source) {
            case 'vessels':
                return `${this.i18N.tr('vessel-activity-text')}`;
            case 'activities':
                return `${this.i18N.tr('tasks-text')}`;
            default:
                {
                    const title = this.router.history.getState("previousRouteName");

                    if (title && title.trim() != "" && title != this.i18N.tr('back-text'))
                        return title;
                    const prevInstruction = this.router.currentInstruction.previousInstruction;
                    return prevInstruction
                        ? prevInstruction.config.title
                        : this.i18N.tr('back-text');
                }
        }
    }


    protected getListRoute(): string {
        return RightShipQiRouterConstants.RouteNames.Activities;
    }

    onBeforeStepChanged(tab: Tabs): Promise<void> {
        return new Promise<void>((resolve, reject) => {
            if (tab && tab.currentTab && tab.currentTab.step == PscStepEnum.PscDocuments) {
                if (tab.currentTab.vm.canDeactivate)
                    tab.currentTab.vm.canDeactivate().then(response => {
                        if (response) {
                            resolve();
                        }
                        else {
                            reject();
                        }
                    }).catch(reject);
            }
            else {
                resolve();
            }
        });

    }

    currentStep: StepModel;
    onStepClicked = (step: StepModel) => {
        this.currentStep = step;
    }

    protected getSteps(): StepModel[] {
        const steps: StepModel[] = [
            {
                lazyLoad: true,
                title: this.i18N.tr("psc-overview-text"),
                step: PscStepEnum.PscOverview,
                vm: this.pscOverview,
                show: true
            }, {
                lazyLoad: true,
                title: this.i18N.tr("psc-deficiencies-text"),
                step: PscStepEnum.PscDeficiencies,
                vm: this.pscDeficiencies,
                show: () => this.item.numberOfDeficiencies > 0 || this.item.deficiencies.length > 0,
            },
            {
                lazyLoad: true,
                title: () => TitleWithCountHelper.getTitle(this.i18N.tr("psc-documents-text"), this.item.documentsCount),
                step: PscStepEnum.PscDocuments,
                vm: this.pscDocuments,
                show: this.canViewPSCDocuments
            },
            {
                lazyLoad: true,
                title: () => TitleWithCountHelper.getTitle("Private Notes", this.item.privateNotesCount),
                show: this.userClaimsService.hasPermission(Claims.CanViewPSCNotes),
                step: PscStepEnum.PscPrivateNotes,
                vm: this.entityComments,
                getModel: () => ({
                    canSubmit: true,
                    entityTypeId: this.item.entityType,
                    entityId: this.item.pscInspectionId,
                    item: this.item
                })
            },
            {
                lazyLoad: true,
                title: () => TitleWithCountHelper.getTitle("Correspondences", this.correspondence.correspondenceCount ?? this.item.correspondenceCount),
                step: PscStepEnum.PscCorrespondence,
                vm: this.correspondence,
                getModel: () => {
                    return this.getCorrespondenceModel();
                },
                show: () => this.viewPSCCorrespondence && this.item ? true : false
            },
            {
                lazyLoad: true,
                title: "Email History",
                step: 5,
                vm: this.emailHistory,
                getModel: () => {
                    return {
                        item: this.item
                    }
                },
                show: () => this.viewPSCEmailHistory && this.item ? true : false
            },
            {
                lazyLoad: true,
                title: this.i18N.tr("history-trail-text"),
                step: PscStepEnum.PscHistoryTrail,
                vm: this.pscHistoryTrail,
                show: this.canViewPSCOverrideHistory
            }
        ];

        steps.forEach(step => {
            step.onClick = this.onBeforeStepChanged;
        });

        return steps;
    }

    private getCorrespondenceModel(): CompCorrespondenceModel {
        return {
            correspondenceEntity: {
                entityId: this.item.pscInspectionId,
                entityTypeId: this.item.entityType,
                isInconsistentState: this.item.isInconsistentState,
                requestedBy: null,
                liaseWithShipManagers: null,
                assignment: {
                    assignedTo: this.item.assignedUserId,
                    assignedToUserName: this.item.assignedTo,
                    assignToMe: this.assignToMe.bind(this),
                    isAssignedToMe: () => this.item.assignedUserId == this.userClientService.currentUser.id,
                }
            },
            name: "PSC",
            showAddRFI: this.item && !this.item.closed && !this.isMisreported && this.userClaimsService.hasPermission(Claims.CanCreateRFIInPSC),
            hasOutcome: this.item && this.item.closed,
            emails: undefined,
            newDocumentAdded: () => {
                this.item.documentsCount += 1;
            },
            showFilters: false
        };
    }

    protected getItem(id: number): Promise<PscClientModel> {
        return this.pscService.getPscClientModel(id);
    }

    protected getItemId(): number {
        return this.item ? this.item.pscInspectionId : null;
    }

    protected saveItem(): Promise<BaseValidationResponse> {
        throw new Error("Method not implemented.");
    }

    protected getUpdateRoute(): string {
        return RightShipQiRouterConstants.RouteNames.PSC;
    }

    protected getEntityType(): EntityTypes {
        return EntityTypes.PSC;
    }

    protected onActivation() {
        this.activeMenuItemsService.setActiveFromMenuItem(this.i18N.tr('tasks-text'));

        if (this._params && this._params.forceLoad) {
            const value = this._params.forceLoad == "true" ? true : false;
            this.forceLoadVesselHeader = value
            this.pscOverview.forceLoad = value;
        }
    }

    protected initializeItemForCreate(): PscClientModel | Promise<PscClientModel> {
        const psc: PscClientModel = {
            rating: null,
            starRating: null,
            pscInspectionStatus: null,
            pscInspectionStatusId: null,
            vesselDateOfBuild: null,
            vesselDwt: null,
            pscInspectionId: null,
            vesselId: null,
            vesselName: null,
            vesselImo: null,
            vesselType: null,
            inspectionDate: null,
            followUpInspection: null,
            numberOfDaysDetained: null,
            shipDetained: null,
            deficiencies: [],
            numberOfDeficiencies: null,
            port: null,
            pscAuthority: null,
            closed: null,
            pscReasonForCloseOut: null,
            misreportedReason: null,
            dataSources: [],
            identifier: Guid.generateGuid(),
            isInconsistentState: null,
            aiExtractionCount: 0,
            enabledNewUI: null
        };

        return psc;
    }

    editPsc() {
        if (this.item && this.item.assignedUserId != this.userClientService.currentUser.id) {
            this.assignToMe().then(() => {
                this.navigateToEditPsc();
            });
        } else {
            this.navigateToEditPsc();
        }
    }

    assignToMe = async () => {
        if (this.assignment) {
            await this.assignment.assignToMe();
            this.item.pscInspectionStatusId = PscInspectionStatusEnum.InProgress;
            this.item.assignedUserId = this.assignment.assignedEntity.assignedTo;
        }
    }

    navigateToEditPsc() {
        this.router.navigateToRoute(RightShipQiRouterConstants.RouteNames.AddPsc,
            {
                id: this.getItemId(),
                vesselId: this.item.vesselId,
                internationalMaritimeOrganizationNumber: this.item.vesselImo,
                vesselName: this.item.vesselName,
                showDetailsSection: "true",
                showDeficienciesSection: "true",
                showAttachmentsSection: "true",
                showCloseOutSection: "false"
            });
    }

    navigateBack() {
        let goBackCount: number = this.router && this.router.history && this.router.history.getState("skipPreviousRoutes") ?
            this.router.history.getState("skipPreviousRoutes") : 0;
        goBackCount++;
        this.cacheClient.put(TasksConstants.TabsCacheKey, TasksStepsEnum.PSC).then(() => {
            this.router.history.go(-goBackCount);
        })
            .catch(() => {
                this.router.history.go(-goBackCount);
            });
    }

    @computedFrom("item")
    get menuItems(): MenuItem[] {
        const visible = this.item ? true : false;
        return [{
            click: () => this.requestRightShipInspection(1),
            isVisible: this.canRequireRightShipInspection && !this.isClosed,
            item: {
                addicon: false,
                editicon: false,
                removeicon: false,
                badgeicon: true,
                optionsicon: false,
                text: 'Requires RightShip inspection'
            },
            classList: '',
        },
        {
            click: () => {
                this.correspondence.activate(this.getCorrespondenceModel());
                this.correspondence.addSubtask(false)
            },
            isVisible: visible && this.userClaimsService.hasPermission(Claims.CanCreateRFIInPSC) && !this.isClosed,
            item: {
                addicon: true,
                editicon: false,
                removeicon: false,
                badgeicon: false,
                optionsicon: false,
                text: 'Create RFI'
            },
            classList: '',
        },
        {
            click: () => {
                this.correspondence.activate(this.getCorrespondenceModel());
                this.correspondence.addSubtask(true)
            },
            isVisible: visible && this.userClaimsService.hasPermission(Claims.CanCreateRFIInPSC) &&
                this.userClaimsService.hasPermission(Claims.CanCreateInternalRfi) && !this.isClosed,
            item: {
                addicon: true,
                editicon: false,
                removeicon: false,
                badgeicon: false,
                optionsicon: false,
                text: 'Create Internal RFI'
            },
            classList: '',
        },
        {
            click: () => this.openOverridePanel(),
            isVisible: visible && (this.item.usedInSafetyScore && this.item.canActivateOrDeactivateSafetyScore && this.userClaimsService.hasPermission(Claims.CanOverridePscDetails)) ||
                (!this.item.usedInSafetyScore && this.userClaimsService.hasPermission(Claims.CanOverridePscDetails)),
            item: {
                addicon: false,
                editicon: false,
                removeicon: false,
                badgeicon: false,
                optionsicon: true,
                text: 'Override PSC details'
            },
            classList: '',
        }
        ].filter(x => x.isVisible);
    }

    requestRightShipInspection(type: number) {
        if (this.item.assignedUserId == this.userClientService.currentUser.id) {
            this.dialogService.show(RightShipDialogServiceConstants.URLs.RequiresRightshipInspectionDialog, {
                vesselName: this.item.vesselName,
                imo: this.item.vesselImo,
                type: type,
                vesselId: this.item.vesselId,
                pscInspectionId: this.item.pscInspectionId,
                shipManagerEmails: this.item.shipManagerEmail ? [this.item.shipManagerEmail] : [],
                reload: (() => {
                    this.getItem(this.getItemId()).then(x => {
                        this.item = x;
                    });
                }),
                id: 0
            }, true, true, false, "right");
        } else {
            this.dialogService.show(RightShipDialogServiceConstants.URLs.VetAssignmentDialog, {
                name: "PSC Inspection",
                submit: this.assignToMe,
                callback: this.requestRightShipInspection.bind(this, type),
                currentAssignment: this.assignment.assignedEntity && this.assignment.assignedEntity.assignedTo ? this.assignment.assignedEntity.assignedToUserName : null
            }, true, false, false);
        }
    }

    onAttached() {
        this.signalClientService.subscribe(SignalFunctions.pscsSynced, this.refreshPsc);
        this.signalClientService.subscribe(SignalFunctions.pscUpdated, this.refreshPsc);
        this.signalClientService.subscribeToGroup(SignalFunctions.entityStatusChanged, SignalFunctions.entityTypeGroup(EntityTypes.PSC), this.refreshPscStatus);
    }

    onDetached() {
        this.signalClientService.unsubscribe(SignalFunctions.pscsSynced, this.refreshPsc);
        this.signalClientService.unsubscribe(SignalFunctions.pscUpdated, this.refreshPsc);
        this.signalClientService.unsubscribeToGroup(SignalFunctions.entityStatusChanged, SignalFunctions.entityTypeGroup(EntityTypes.PSC), this.refreshPscStatus);
    }

    refreshPscStatus = (param: EntityStatusChangeModel) => {
        if (this.item.pscInspectionStatusId == PscInspectionStatusEnum.Closed) return;
        if (this.item.pscInspectionId == param.entityId) {

            if (this.assignment && param.statusId != PscInspectionStatusEnum.PscInspectionReassignment) {
                this.assignment.reload().then(x => this.item.assignedUserId = x.assignedTo);
            }

            const pscInspectionStatus = this.pscInspectionStatuses.filter(x => x.id == param.statusId)[0];

            if (pscInspectionStatus) {
                this.item.pscInspectionStatus = pscInspectionStatus.name;
                this.item.pscInspectionStatusId = pscInspectionStatus.id;
                this.item.closed = pscInspectionStatus.id == PscInspectionStatusEnum.Closed;
                this.item.statuses.splice(0, 0, {
                    previousStatusId: this.currentStatus ? this.currentStatus.statusId : null,
                    date: new Date(),
                    name: pscInspectionStatus.name,
                    statusId: pscInspectionStatus.id,
                    user: param.userName ? param.userName : "",
                    subtaskTypes: [],
                    comment: param.statusSnippet
                });
            }

            if (this.isMisreported && param.misreportedReasonId) {
                const misreportedReason = this.pscInspectionMisreportedReasons.filter(x => x.id == param.misreportedReasonId)[0];
                if (misreportedReason)
                    this.item.misreportedReason = misreportedReason.name;
            }
        }
    }

    refreshPsc = (params) => {
        if (params && params.pscIds && params.pscIds.indexOf(this.item.pscInspectionId) > -1) {
            this.getItem(this.getItemId()).then(x => {
                this.item = x;
            }).catch(e => {
                console.error("Failed To refresh Psc Details. error: " + e.message);
            });
        }
    }

    openPscInspectionStatusDialog() {
        if (this.canChangePscInspectionStatus) {
            if (!this.item.closed || (this.reopeningPsc && this.item.closed)) {
                if (this.item.assignedUserId == this.userClientService.currentUser.id || this.item.closed) {
                    this.dialogService.show(RightShipDialogServiceConstants.URLs.PscInspectionStatusDialog,
                        {
                            pscInspectionId: this.item.pscInspectionId,
                            pscInspectionStatusId: this.item.pscInspectionStatusId,
                            reload: (() => {
                                this.getItem(this.getItemId()).then(x => {
                                    this.item = x;
                                    this.refreshSteps();
                                });
                            }),
                            vesselName: this.item.vesselName,
                            internationalMaritimeOrganizationNumber: this.item.vesselImo,
                            europeanNumberOfIdentification: this.item.europeanNumberOfIdentification,
                            vehicleIdentificationNumber: this.item.vehicleIdentificationNumber,
                            deficiencies: this.item.deficiencies,
                            viewDeficienciesTab: this.viewDeficienciesTab.bind(this),
                            enabledNewUI: this.item.enabledNewUI
                        }, true, false, false, 'right');
                } else {
                    this.dialogService.show(RightShipDialogServiceConstants.URLs.VetAssignmentDialog, {
                        name: "PSC Inspection",
                        submit: this.assignToMe,
                        callback: this.openPscInspectionStatusDialog.bind(this),
                        currentAssignment: this.assignment.assignedEntity && this.assignment.assignedEntity.assignedTo ? this.assignment.assignedEntity.assignedToUserName : null
                    }, true, false, false);
                }
            }
            else if (this.item.closed && !this.reopeningPsc) {
                this.notificationService.show("You do not have permission to reopen a closed PSC", "", NotificationType.Warning);
            }
        }
    }

    copy(str: string) {
        this.clipboardCopyService.copy(str);
    }

    public async canDeactivate(): Promise<boolean> {
        if (this.currentStep && this.currentStep.vm && this.currentStep.vm.canDeactivate) {
            return await this.currentStep.vm.canDeactivate();
        }
        else {
            return true;
        }
    }

    getPscStatusHeaderClass(item: { entityTypeId: number, numberOfDeficiencies: number, shipDetained: boolean, concatenatedDataSources: string, pscInspectionStatus: string, statusId: number, pscInspectionStatusId: number }) {
        if (!item.entityTypeId)
            item.entityTypeId = EntityTypes.PSC;
        if ((PscActivitiesColumns.getIsCleanAndIHSPsc(item) && this.rightShipUser) || (PscActivitiesColumns.getIsCleanPsc(item) && !this.rightShipUser)) {
            return "clean";
        }

        if (item.pscInspectionStatus) {
            return item.pscInspectionStatus.split(' ').join('-').toLowerCase();
        }
    }

    getPscStatusHeader(item: { entityTypeId: number, numberOfDeficiencies: number, shipDetained: boolean, concatenatedDataSources: string, pscInspectionStatus: string, statusId: number, pscInspectionStatusId: number }) {
        if (!item.entityTypeId)
            item.entityTypeId = EntityTypes.PSC;
        if (PscActivitiesColumns.getIsCleanAndIHSPsc(item) && this.rightShipUser) {
            return "Created";
        }

        if (PscActivitiesColumns.getIsCleanPsc(item) && !this.rightShipUser) {
            return "Clean"
        }

        return item.pscInspectionStatus;
    }

    openOverridePanel() {
        this.dialogService.show(RightShipDialogServiceConstants.URLs.PscOverrideDetailsDialog, {
            item: this.item,
            reload: (() => {
                this.getItem(this.getItemId()).then(x => {
                    this.item = x;
                    this.refreshSteps();
                });
            })
        }, true, false, false, 'right')
    }

    viewDeficienciesTab() {
        const step = this.steps.filter(x => x.step == PscStepEnum.PscDeficiencies)[0];
        this.entityItem.currentStep = step;
    }
}